import type { GraphPath } from '../GraphPath';
import type { AvailableGraphEntities } from '../availableEntities';
import type { EdgeRecord } from '../edges/EdgeData';
import type { GraphOverlay } from '../graphOverlay/GraphOverlay';
import type { PathLookupGraphSettings } from '../graphSettings/PathLookupGraphPreset';
import type { TopologyGraphSettings } from '../graphSettings/TopologyGraphPreset';
import type { BoxLabels, NodeRecord } from '../nodes';
import type { Layout } from './GraphLayoutRequest';
import type { GraphPathLookupResult } from './GraphPathLookupResult';

export interface GenericGraphData {
  nodes: NodeRecord;
  edges: EdgeRecord;
}

export interface GraphResult<GD extends GenericGraphData = GenericGraphData> {
  boxLabels: BoxLabels;
  settings: TopologyGraphSettings | PathLookupGraphSettings;
  graphData: GD;
}

export interface GraphPathInfo {
  defaultLayout: Layout;
  layoutAlgorithm: Layout;
}

export interface GraphResponseBody<
  GD extends GenericGraphData = GenericGraphData,
> {
  overlay?: GraphOverlay;
  graphResult: GraphResult<GD>;
  pathlookup?: GraphPathLookupResult;
  topology?: {
    availableEntities: AvailableGraphEntities;
    /**
     * Indicates that all paths available in the snapshot are selected,
     * so there are no remaining paths to be shown in "All network".
     */
    allPathsSelected: boolean;
    /**
     * List of requested paths existing in the snapshot
     *
     * This list contains also paths that exist, but no nodes were returned for e.g. due to filters.
     * It lets user know whether he's querying for non-existent paths, or just all content
     * from given paths is filtered out.
     */
    paths: {
      [key in GraphPath]: GraphPathInfo;
    };
  };
  emptyGraphReason?: string;
}
